import { InternalNode } from '@xyflow/react'

import NodeType from 'types/transformation/enums/NodeType'
import { Node } from 'types/transformation/types/nodes'

const COLUMN_GAP = 80
const BRANCH_GAP = 40

type Side = 'left' | 'right'

const SIDES: Side[] = ['left', 'right']

type HandleBox = { id?: string | null; y: number; height: number }

/**
 * A rendered node's size and the vertical centres of its handles, relative to
 * the node's top edge.
 */
export interface NodeBox {
  width: number
  height: number
  outputY: number
  inputY: Record<Side, number>
}

const EMPTY_BOX: NodeBox = {
  width: 0,
  height: 0,
  outputY: 0,
  inputY: { left: 0, right: 0 }
}

interface Subtree {
  ys: Map<string, number>
  top: number
  bottom: number
  outputY: number
}

function handleCenter(handle: HandleBox | undefined, fallback: number) {
  return handle ? handle.y + handle.height / 2 : fallback
}

/**
 * Reads a node's box from React Flow's measurements. A node without measured
 * handles gets them at its vertical centre, where the board draws them.
 */
export function getNodeBox(
  node: Node,
  internal: InternalNode<Node> | undefined
): NodeBox {
  const width = node.measured?.width ?? 0
  const height = node.measured?.height ?? 0
  const center = height / 2
  const source = internal?.internals.handleBounds?.source ?? []
  const target = internal?.internals.handleBounds?.target ?? []

  return {
    width,
    height,
    outputY: handleCenter(source[0], center),
    inputY: {
      left: handleCenter(
        target.find((handle) => handle.id === 'left') ?? target[0],
        center
      ),
      right: handleCenter(
        target.find((handle) => handle.id === 'right') ?? target[0],
        center
      )
    }
  }
}

function inputRef(node: Node, side: Side): string | null {
  const data = node.data as Partial<Record<`${Side}_node`, string | null>>

  return data[`${side}_node`] ?? null
}

/**
 * Lays the board out left to right along the data flow. Every node's output
 * feeds at most one node, so the board is a set of trees rooted at the nodes
 * nobody reads from (the MAP node first). Each node sits one column left of
 * the node it feeds, branches stack below each other without overlapping, and
 * a node lines its first input handle up with that input's output handle so
 * chains of steps run in a straight line.
 */
export function layoutBoard(
  nodes: Node[],
  boxes: Map<string, NodeBox>
): Node[] {
  const byId = new Map(nodes.map((node) => [node.id, node]))

  const inputsOf = (node: Node) =>
    SIDES.flatMap((side) => {
      const id = inputRef(node, side)
      const input = id ? byId.get(id) : undefined

      return input ? [{ side, input }] : []
    })

  const fed = new Set(
    nodes.flatMap((node) => inputsOf(node).map(({ input }) => input.id))
  )

  const isMap = (node: Node) => Number(node.data.type === NodeType.MAP)

  const roots = nodes
    .filter((node) => !fed.has(node.id))
    .sort((a, b) => isMap(b) - isMap(a) || a.data.index - b.data.index)

  const depths = new Map<string, number>()

  const place = (node: Node, depth: number): Subtree => {
    depths.set(node.id, depth)

    const box = boxes.get(node.id) ?? EMPTY_BOX
    const ys = new Map<string, number>()
    let top = Infinity
    let bottom = -Infinity
    let y: number | null = null

    for (const { side, input } of inputsOf(node)) {
      if (depths.has(input.id)) continue

      const branch = place(input, depth + 1)
      const offset = bottom === -Infinity
        ? 0
        : bottom + BRANCH_GAP - branch.top

      branch.ys.forEach((branchY, id) => ys.set(id, branchY + offset))
      top = Math.min(top, branch.top + offset)
      bottom = Math.max(bottom, branch.bottom + offset)
      y = y ?? branch.outputY + offset - box.inputY[side]
    }

    const nodeY = y ?? 0

    ys.set(node.id, nodeY)

    return {
      ys,
      top: Math.min(top, nodeY),
      bottom: Math.max(bottom, nodeY + box.height),
      outputY: nodeY + box.outputY
    }
  }

  const ys = new Map<string, number>()
  let bottom = -Infinity

  const placeTree = (root: Node) => {
    const tree = place(root, 0)
    const offset = bottom === -Infinity
      ? -tree.top
      : bottom + BRANCH_GAP - tree.top

    tree.ys.forEach((y, id) => ys.set(id, y + offset))
    bottom = tree.bottom + offset
  }

  roots.forEach(placeTree)

  // Whatever is left sits on a cycle, which no root reaches.
  nodes.forEach((node) => {
    if (!depths.has(node.id)) placeTree(node)
  })

  const maxDepth = Math.max(0, ...depths.values())
  const columnWidths = Array.from({ length: maxDepth + 1 }, () => 0)

  nodes.forEach((node) => {
    const depth = depths.get(node.id) ?? 0

    columnWidths[depth] = Math.max(
      columnWidths[depth],
      (boxes.get(node.id) ?? EMPTY_BOX).width
    )
  })

  const columnX: number[] = []
  let x = 0

  for (let depth = maxDepth; depth >= 0; depth--) {
    columnX[depth] = x
    x += columnWidths[depth] + COLUMN_GAP
  }

  return nodes.map((node) => ({
    ...node,
    position: {
      x: columnX[depths.get(node.id) ?? 0],
      y: Math.round(ys.get(node.id) ?? 0)
    }
  }))
}
